import React from "react";
import { Link } from "react-router-dom";
import { User, Settings, LayoutDashboard, LogOut, MessageSquare, Bell, HelpCircle, IdCard } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

const initials = (u) => {
    if (!u) return "?";
    const src = u.name || u.email || "";
    return src.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
};

const ROLE_LABEL = {
    professional: "Talent",
    employer: "Employer",
    recruiter: "Recruiter",
    admin: "Admin",
};

export const ProfileMenu = () => {
    const { user, logout } = useAuth();
    if (!user) return null;
    const role = ROLE_LABEL[user.role] || user.role || "";

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="profile-menu-trigger"
                    className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-border hover:bg-muted transition-colors"
                >
                    <Avatar className="w-7 h-7">
                        <AvatarFallback className="bg-brand-green text-white text-xs font-bold">{initials(user)}</AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-medium text-foreground hidden sm:inline">{user.name?.split(" ")[0] || user.email?.split("@")[0]}</span>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="profile-menu" className="w-64 p-2">
                <div className="px-3 py-3 border-b border-border mb-2">
                    <div className="font-semibold text-sm text-foreground truncate">{user.name || "Signed in"}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    {role && <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase">{role}</div>}
                </div>
                <DropdownMenuItem asChild>
                    <Link to="/dashboard" data-testid="profile-menu-dashboard" className="cursor-pointer">
                        <LayoutDashboard size={14} className="mr-2" /> Dashboard
                    </Link>
                </DropdownMenuItem>
                {user.role === "professional" && (
                    <DropdownMenuItem asChild>
                        <Link to="/talent" className="cursor-pointer">
                            <IdCard size={14} className="mr-2" /> Talent Passport
                        </Link>
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                    <Link to="/inbox" data-testid="profile-menu-inbox" className="cursor-pointer">
                        <MessageSquare size={14} className="mr-2" /> Messages
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                    <Link to="/notifications" data-testid="profile-menu-notifications" className="cursor-pointer">
                        <Bell size={14} className="mr-2" /> Notifications
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                    <Link to="/settings" data-testid="profile-menu-settings" className="cursor-pointer">
                        <Settings size={14} className="mr-2" /> Settings
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                    <Link to="/contact" data-testid="profile-menu-support" className="cursor-pointer">
                        <HelpCircle size={14} className="mr-2" /> Support
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={logout}
                    data-testid="profile-menu-logout"
                    className="cursor-pointer text-destructive focus:text-destructive"
                >
                    <LogOut size={14} className="mr-2" /> Log out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ProfileMenu;
